import { useRouter } from 'next/router';
import queryString from 'query-string';
import { useEffect, useRef } from 'react';

import {
  API_ENDPOINT,
  useClientApiV1AuthSsoRedirectQuery,
} from '../../resources/@types/accountSchema';
import { antmanFetcher } from '../../resources/Fetcher';
import { useReferUrlNavigation } from '../hooks/useReferUrlNavigation';

const MAX_DELAY_MS = 500;

function useClientAuthSsoRedirectQuery(
  options?: Parameters<typeof useClientApiV1AuthSsoRedirectQuery>[0]['options'],
  isFirst = false,
) {
  const { getReferUrl, navigateMainUrl } = useReferUrlNavigation();
  const router = useRouter();
  const userDiscountPublishRef = useRef<Promise<unknown> | null>(null);

  const { data, isSuccess, isError } = useClientApiV1AuthSsoRedirectQuery({
    queryKey: [API_ENDPOINT.CLIENT_V1_AUTH_SSO_REDIRECT],
    url: queryString.stringifyUrl({
      url: API_ENDPOINT.CLIENT_V1_AUTH_SSO_REDIRECT,
      query: {
        ...router.query,
        referUrl: getReferUrl(isFirst),
      },
    }),
    options,
  });

  useEffect(() => {
    if (!isFirst && isError) {
      navigateMainUrl();
    }
  }, [isError, isFirst, navigateMainUrl]);

  // 신규 가입 플로우(isFirst) SSO redirect 직전에 개별할인 발행을 트리거한다.
  // SIGNUP UserDiscount가 INSERT돼 있어야 redirect 후 강의 상세의 paymentInfo가
  // 첫 조회부터 할인가를 응답할 수 있다.
  //
  // 레거시 모놀리스(ant-man)의 발행 트리거에서 header-api의 헤더 공지 조회로 바꿨다.
  // 발행 로직은 이관되면서 그대로 옮겨졌고(UserDiscountPublishService), 이 엔드포인트가
  // 매 페이지 로드마다 같은 발행을 수행한다. 다만 회원가입 직후에는 강의 상세보다 먼저
  // 발행돼야 하므로 여기서 명시적으로 한 번 호출해 순서를 보장한다.
  //
  // 주의: 응답 본문(헤더 공지)은 쓰지 않는다. 필요한 것은 발행과 Set-Cookie(userDC)뿐이다.
  // 이 호출을 없애면 발행이 강의 상세 조회 뒤로 밀려 정가가 노출된다.
  //
  // TODO: 회원가입 시 백엔드가 가입환영 할인을 직접 발행하도록 바뀌면 이 호출을 제거한다.
  // (회원가입 인증 시점에 서버가 발행하면 프론트가 순서를 보장할 필요가 없어진다)
  useEffect(() => {
    if (isFirst && options?.enabled && !userDiscountPublishRef.current) {
      userDiscountPublishRef.current = antmanFetcher
        .get('/v1/notice/exposure')
        .catch(() => undefined);
    }
  }, [isFirst, options?.enabled]);

  useEffect(() => {
    /**
     * @see {@link https://github.com/inflearn/inflearn-ucc-frontend/pull/415}
     * 여러 컴포넌트에서 같은 쿼리를 사용하고 있어 isSuccess 상태가 공유되고 있음 (queryKey가 같음)
     * options.enabled가 false인데도 다른 곳에서 성공할 경우 아래 로직이 실행될 수 있음
     * 따라서 options.enabled가 true인지 확인하는 조건이 필요함
     */
    if (!options?.enabled || !isSuccess || !data) {
      return;
    }

    const doRedirect = () => {
      location.href = data.redirectUrl;
    };

    if (!isFirst || !userDiscountPublishRef.current) {
      doRedirect();
      return;
    }

    // 발행이 완료되면 redirect를 진행하되, MAX_DELAY_MS를 통해 최대 지연 시간을 제한한다
    let settled = false;
    const go = () => {
      if (settled) {
        return;
      }
      settled = true;
      doRedirect();
    };
    const timer = setTimeout(go, MAX_DELAY_MS);
    userDiscountPublishRef.current.finally(() => {
      clearTimeout(timer);
      go();
    });
  }, [isSuccess, data, options?.enabled, isFirst]);
}

useClientAuthSsoRedirectQuery.queryKey = [API_ENDPOINT.CLIENT_V1_AUTH_SSO_REDIRECT];

export default useClientAuthSsoRedirectQuery;
